import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { toast } from "@/components/ui/toast";
import { ITransplantConfigPanel } from "@/features/admin/components/itransplant/ITransplantConfigPanel";
import { ITransplantOrgsPanel } from "@/features/admin/components/itransplant/ITransplantOrgsPanel";
import { IntegrationMessagesGrid } from "@/features/integrations/components/IntegrationMessagesGrid";
import { Section } from "../components/Section";
import { useTabSearch } from "../hooks";

export function ITransplantAdminPage() {
  const tabs = useTabSearch("orgs");

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/integrations" }}>
              Integrations
            </Breadcrumb>
            <Breadcrumb>iTransplant</Breadcrumb>
          </>
        }
        actions={<GlobalEnableSwitch />}
      >
        iTransplant
      </Section.Title>
      <Tabs {...tabs} className="flex flex-col gap-4 flex-1 min-h-0">
        <TabsList
          className="w-full shrink-0 justify-start"
          aria-label="iTransplant tabs"
        >
          <Tab id="orgs">Organizations</Tab>
          <Tab id="messages">Messages</Tab>
          <Tab id="config">Configuration</Tab>
        </TabsList>
        <TabPanel id="orgs" className="flex flex-col flex-1 min-h-0">
          <ITransplantOrgsPanel />
        </TabPanel>
        <TabPanel id="messages" className="flex flex-col flex-1 min-h-0">
          <MessagesPanel />
        </TabPanel>
        <TabPanel id="config" className="min-h-0 overflow-y-auto">
          <ITransplantConfigPanel />
        </TabPanel>
      </Tabs>
    </>
  );
}

function GlobalEnableSwitch() {
  const utils = trpc.useUtils();
  const { data } = trpc.integrations.get.useQuery({ kind: "ITRANSPLANT" });
  const mutation = trpc.integrations.setEnabled.useMutation({
    onSuccess: () => {
      utils.integrations.get.invalidate({ kind: "ITRANSPLANT" });
      utils.integrations.list.invalidate();
    },
    onError: (err) => {
      toast.error("Failed to update integration", {
        description: err.message,
      });
    },
  });

  const enabled = !!data?.enabled;

  return (
    <div className="flex items-center gap-3">
      <Badge color={enabled ? "green" : "gray"} className="shrink-0">
        {enabled ? "Enabled" : "Disabled"}
      </Badge>
      <Switch
        aria-label="Enable iTransplant integration globally"
        isSelected={enabled}
        isDisabled={!data || mutation.isPending}
        onChange={(next) =>
          mutation.mutate({ kind: "ITRANSPLANT", enabled: next })
        }
      />
    </div>
  );
}

function MessagesPanel() {
  return (
    <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <CardHeader>
        <CardTitle>Outbound messages</CardTitle>
        <CardDescription>
          Every send attempt for the iTransplant integration. Use the panel icon
          on any row to view the full request and response, or to retry a failed
          message.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 min-h-0 p-0! border-t">
        <IntegrationMessagesGrid kind="ITRANSPLANT" />
      </CardContent>
    </Card>
  );
}
